/**
 * Thingiverse Multi-File Importer with Three.js Integration Fix
 * This script imports STL files from Thingiverse and displays multiple parts side by side
 * Includes fixes for issues with overlapping parts, scene clearing, and ensures Three.js is properly initialized
 */
document.addEventListener('DOMContentLoaded', function() {
  console.log('Thingiverse Multi-File Importer with Three.js Integration Fix - Initializing');
  
  // Configuration
  const ACCESS_TOKEN = "f712284f405040c7f34f47c8593e2dae";
  
  // Track if we've added our custom elements
  let elementsAdded = false;
  // Keep track of download attempts to prevent loops
  let pendingDownloads = {};
  // Keep track of loaded model files
  let loadedModelFiles = [];
  
  // Track initialization status for Three.js
  let initialized = false;
  let initializationAttempts = 0;
  const MAX_ATTEMPTS = 10;
  
  // Track loaded files to prevent duplicates
  // Use a global Map to store file URLs that have been loaded
  const alreadyLoadedUrls = new Map();
  
  // Flag to track if a load operation is in progress
  let isLoading = false;
  
  // Initialize the integration
  function initialize() {
    if (elementsAdded) return;
    
    // 1. Add direct import button to upload section
    addDirectImportButton();
    
    // 2. Listen for model selection events
    listenForModelSelection();
    
    // 3. Add event listeners to model buttons
    addModelCardListeners();
    
    // 4. Set up auto-detection for downloaded files
    setupFileDetection();
    
    // 5. Create a container for multi-model controls if needed
    addMultiModelControls();
    
    // 6. Extend the Three.js environment with STL utilities if needed
    extendThreeJsEnvironment();
    
    // 7. Add debug logging functions to window for easy access
    addDebugFunctions();
    
    // 8. Ensure Three.js environment is initialized
    initializeThreeJsEnvironment();
    
    elementsAdded = true;
    console.log('Thingiverse Multi-File Importer initialized');
  }
  
  // Function to check if Three.js is available
  function isThreeJsAvailable() {
    return typeof THREE !== 'undefined';
  }
  
  // Function to check if viewer container exists
  function doesViewerExist() {
    return document.getElementById('viewer') !== null;
  }
  
  // Function to ensure scene exists and is accessible
  function ensureSceneExists() {
    // If we're already initialized, just return the scene
    if (initialized && window.scene) {
      return true;
    }
    
    // Try to initialize if not already done
    return initializeThreeJsEnvironment();
  }
  
  // Function to safely get the scene
  function getScene() {
    if (!ensureSceneExists()) {
      throw new Error('Could not access or create Three.js scene');
    }
    return window.scene;
  }
  
  // Main Three.js initialization function
  function initializeThreeJsEnvironment() {
    if (initialized) return true;
    if (initializationAttempts >= MAX_ATTEMPTS) {
      console.error('Max initialization attempts reached - giving up');
      return false;
    }
    
    initializationAttempts++;
    console.log(`Initialization attempt ${initializationAttempts}/${MAX_ATTEMPTS}`);
    
    // Check prerequisites
    if (!isThreeJsAvailable()) {
      console.warn('THREE.js not yet available, will retry');
      return false;
    }
    
    if (!doesViewerExist()) {
      console.warn('Viewer container not yet available, will retry');
      return false;
    }
    
    try {
      console.log('Prerequisites satisfied, creating Three.js environment');
      
      // 1. Create scene if it doesn't exist
      if (!window.scene) {
        window.scene = new THREE.Scene();
        window.scene.background = new THREE.Color(0xf0f0f0);
        console.log('Created new scene');
      }
      
      // 2. Get viewer dimensions
      const viewerContainer = document.getElementById('viewer');
      const width = viewerContainer.clientWidth || 400;
      const height = viewerContainer.clientHeight || 400;
      
      // 3. Create camera if it doesn't exist
      if (!window.camera) {
        window.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 3000);
        window.camera.position.set(300, 200, 300);
        window.camera.lookAt(0, 0, 0);
        console.log('Created new camera');
      }
      
      // 4. Create renderer if it doesn't exist
      if (!window.renderer) {
        window.renderer = new THREE.WebGLRenderer({ antialias: true });
        window.renderer.setSize(width, height);
        viewerContainer.appendChild(window.renderer.domElement);
        console.log('Created new renderer');
      }
      
      // 5. Add grid if it doesn't exist
      if (!window.grid) {
        window.grid = new THREE.GridHelper(420, 42, 0x888888, 0xcccccc);
        window.scene.add(window.grid);
        console.log('Added grid to scene');
      }
      
      // 6. Add lights if they don't exist
      if (!window.ambientLight) {
        window.ambientLight = new THREE.AmbientLight(0x404040, 1.5);
        window.scene.add(window.ambientLight);
        
        window.directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
        window.directionalLight1.position.set(1, 1, 1);
        window.scene.add(window.directionalLight1);
        
        window.directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
        window.directionalLight2.position.set(-1, 0.5, -1);
        window.scene.add(window.directionalLight2);
        
        console.log('Added lights to scene');
      }
      
      // 7. Set up array for loaded model parts if it doesn't exist
      if (!window.loadedModelParts) {
        window.loadedModelParts = [];
        console.log('Initialized loadedModelParts array');
      }
      
      // 8. Define predefined colors if they don't exist
      if (!window.predefinedColors) {
        window.predefinedColors = [
          0x2196F3, // Blue
          0xF44336, // Red
          0x4CAF50, // Green
          0xFF9800, // Orange
          0x9C27B0, // Purple
          0x00BCD4, // Cyan
          0xFFEB3B, // Yellow
          0x795548, // Brown
          0x607D8B, // Blue Grey
          0x009688, // Teal
          0xE91E63, // Pink
          0x673AB7  // Deep Purple
        ];
        console.log('Defined predefined colors');
      }
      
      // 9. Create a test render to ensure everything works
      window.renderer.render(window.scene, window.camera);
      console.log('Performed test render');
      
      // 10. Setup orbit controls to enable model rotation
      setupSimpleOrbitControls();
      
      // Mark as initialized
      initialized = true;
      console.log('Three.js environment successfully initialized!');
      
      // Handle window resize
      window.addEventListener('resize', handleWindowResize);
      console.log('Added window resize handler');
      
      return true;
    } catch (error) {
      console.error('Error initializing Three.js environment:', error);
      return false;
    }
  }
  
  // Handle window resize
  function handleWindowResize() {
    if (!initialized) return;
    
    const viewerContainer = document.getElementById('viewer');
    if (!viewerContainer || !window.renderer || !window.camera) return;
    
    const width = viewerContainer.clientWidth;
    const height = viewerContainer.clientHeight;
    
    window.camera.aspect = width / height;
    window.camera.updateProjectionMatrix();
    window.renderer.setSize(width, height);
    window.renderer.render(window.scene, window.camera);
  }
  
  // Setup basic orbit controls to enable model rotation
  function setupSimpleOrbitControls() {
    const viewer = document.getElementById('viewer');
    if (!viewer || !window.camera || !window.scene || !window.renderer) return;
    
    let isDragging = false;
    let previousMousePosition = { x: 0, y: 0 };
    
    // Set up mouse event listeners
    viewer.addEventListener('mousedown', function(e) {
      isDragging = true;
      previousMousePosition = { x: e.clientX, y: e.clientY };
      viewer.style.cursor = 'grabbing';
    });
    
    document.addEventListener('mouseup', function() {
      isDragging = false;
      viewer.style.cursor = 'grab';
    });
    
    document.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      
      const deltaMove = {
        x: e.clientX - previousMousePosition.x,
        y: e.clientY - previousMousePosition.y
      };
      
      const rotationSpeed = 0.005;
      
      // Rotate around y-axis for horizontal movement
      const horizontalRotation = rotationSpeed * deltaMove.x;
      window.camera.position.x = window.camera.position.x * Math.cos(horizontalRotation) + 
                            window.camera.position.z * Math.sin(horizontalRotation);
      window.camera.position.z = window.camera.position.z * Math.cos(horizontalRotation) -
                            window.camera.position.x * Math.sin(horizontalRotation);
      
      // Limit vertical rotation to avoid going under the model
      const verticalRotation = rotationSpeed * deltaMove.y;
      const newY = window.camera.position.y - verticalRotation * 5;
      window.camera.position.y = Math.max(10, Math.min(300, newY));
      
      // Keep camera looking at center
      window.camera.lookAt(0, 0, 0);
      
      previousMousePosition = { x: e.clientX, y: e.clientY };
      
      // Force render update
      window.renderer.render(window.scene, window.camera);
    });
    
    // Add wheel zoom
    viewer.addEventListener('wheel', function(e) {
      e.preventDefault();
      
      const zoomFactor = 0.1;
      const direction = e.deltaY > 0 ? 1 : -1;
      
      // Get current distance from center
      const distance = Math.sqrt(
        window.camera.position.x * window.camera.position.x +
        window.camera.position.y * window.camera.position.y +
        window.camera.position.z * window.camera.position.z
      );
      
      // Calculate new distance
      const newDistance = Math.max(50, Math.min(1000, distance * (1 + direction * zoomFactor)));
      const ratio = newDistance / distance;
      
      // Apply zoom by scaling camera position
      window.camera.position.x *= ratio;
      window.camera.position.y *= ratio;
      window.camera.position.z *= ratio;
      
      // Keep camera looking at center
      window.camera.lookAt(0, 0, 0);
      
      // Force render update
      window.renderer.render(window.scene, window.camera);
    });
    
    // Set initial cursor style
    viewer.style.cursor = 'grab';
  }
  
  // Add debugging functions to window
  function addDebugFunctions() {
    // Debug function to check scene contents
    window.debugScene = function() {
      if (window.scene) {
        console.log('Scene children:', window.scene.children.length);
        window.scene.children.forEach((child, i) => {
          if (child.type === 'Mesh') {
            console.log(`Mesh ${i}:`, {
              position: child.position,
              visible: child.visible,
              material: child.material ? child.material.color : 'No material'
            });
          } else {
            console.log(`Child ${i}:`, child.type);
          }
        });
      } else {
        console.log('Scene not available');
      }
    };
    
    // Debug function to check part visibility
    window.checkPartVisibility = function() {
      if (window.loadedModelParts) {
        console.log('Loaded parts:', window.loadedModelParts.length);
        window.loadedModelParts.forEach((part, i) => {
          console.log(`Part ${i} (${part.name}) visible:`, part.model ? part.model.visible : 'No model');
        });
      } else {
        console.log('No loaded parts array');
      }
    };
  }
  
  // Extend the Three.js environment with necessary utilities
  function extendThreeJsEnvironment() {
    // Create global array for loaded parts if it doesn't exist
    if (typeof window.loadedModelParts === 'undefined') {
      window.loadedModelParts = [];
    }
    
    // Add support for handling STL files if THREE.STLLoader doesn't exist
    if (window.THREE && !window.THREE.STLLoader) {
      // Create a basic STL loader
      window.THREE.STLLoader = function() {
        this.parse = function(data) {
          const geometry = parseSTLBinary(data);
          return geometry;
        };
      };
    }
    
    // Add the arrangeModels function to window
    window.arrangeModels = arrangeModels;
    window.checkAndFixPartPositions = checkAndFixPartPositions;
  }
  
  // Add a direct import button to the upload section
  function addDirectImportButton() {
    const uploadSection = document.getElementById('uploadSection');
    if (!uploadSection || document.getElementById('thingiverse-direct-btn')) {
      return;
    }
    
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'thingiverse-direct-container';
    buttonContainer.style.marginTop = '15px';
    buttonContainer.style.textAlign = 'center';
    
    const button = document.createElement('button');
    button.id = 'thingiverse-direct-btn';
    button.textContent = 'Import from Thingiverse';
    button.style.padding = '10px 15px';
    button.style.backgroundColor = '#2196F3';
    button.style.color = 'white';
    button.style.border = 'none';
    button.style.borderRadius = '4px';
    button.style.cursor = 'pointer';
    button.style.fontWeight = '500';
    
    button.addEventListener('click', function() {
      const input = prompt('Enter Thingiverse ID or URL:');
      if (!input) return;
      
      let thingId = input.trim();
      
      // Extract ID from URL if necessary
      if (thingId.includes('thingiverse.com')) {
        const match = thingId.match(/thing[:/](\d+)/);
        if (match && match[1]) {
          thingId = match[1];
        }
      }
      
      // Validate ID
      if (!/^\d+$/.test(thingId)) {
        alert('Please enter a valid Thingiverse ID number');
        return;
      }
      
      // Show loading
      showLoading(`Importing model #${thingId} from Thingiverse...`);
      
      // Import the model
      importThingiverseModel(thingId);
    });
    
    buttonContainer.appendChild(button);
    uploadSection.appendChild(buttonContainer);
  }
  
  // Add controls for multi-model display
  function addMultiModelControls() {
    // Check if we already have the controls
    if (document.getElementById('multi-model-controls')) {
      return;
    }
    
    // Find a good place to insert the controls
    const viewerContainer = document.getElementById('viewer');
    if (!viewerContainer) {
      console.warn('Could not find viewer container for multi-model controls');
      return;
    }
    
    const controlsContainer = document.createElement('div');
    controlsContainer.id = 'multi-model-controls';
    controlsContainer.style.position = 'absolute';
    controlsContainer.style.top = '10px';
    controlsContainer.style.right = '10px';
    controlsContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.85)';
    controlsContainer.style.padding = '8px';
    controlsContainer.style.borderRadius = '4px';
    controlsContainer.style.display = 'none'; // Hidden by default
    controlsContainer.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    controlsContainer.style.backdropFilter = 'blur(5px)';
    controlsContainer.style.maxWidth = '220px';
    
    controlsContainer.innerHTML = `
      <div style="margin-bottom: 8px; font-weight: bold; font-size: 13px; color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px;">Multi-Part Controls</div>
      <div style="display: flex; gap: 5px; margin-bottom: 10px;">
        <button id="arrange-models-btn" style="padding: 4px 8px; flex: 1; background: #2196F3; color: white; border: none; border-radius: 3px; font-size: 12px; cursor: pointer;">Arrange Models</button>
        <button id="reset-view-btn" style="padding: 4px 8px; flex: 1; background: #757575; color: white; border: none; border-radius: 3px; font-size: 12px; cursor: pointer;">Reset View</button>
      </div>
      <div style="text-align: center; margin-bottom: 5px;">
        <button id="debug-scene-btn" style="padding: 3px 6px; font-size: 10px; background: #f8f8f8; border: 1px solid #ddd; border-radius: 2px; cursor: pointer;">Debug Scene</button>
      </div>
      <div id="model-parts-list" style="margin-top: 8px; max-height: 150px; overflow-y: auto; font-size: 12px;"></div>
    `;
    
    // Find a parent container for the viewer
    const parentContainer = viewerContainer.parentElement;
    if (parentContainer) {
      parentContainer.style.position = 'relative';
      parentContainer.appendChild(controlsContainer);
    } else {
      viewerContainer.parentNode.insertBefore(controlsContainer, viewerContainer.nextSibling);
    }
    
    // Add event listeners
    document.getElementById('arrange-models-btn').addEventListener('click', function() {
      arrangeModels();
    });
    
    document.getElementById('reset-view-btn').addEventListener('click', function() {
      resetModelView();
    });
    
    document.getElementById('debug-scene-btn').addEventListener('click', function() {
      if (window.debugScene) {
        window.debugScene();
      }
      if (window.checkPartVisibility) {
        window.checkPartVisibility();
      }
    });
    
    // Expose function to update UI
    window.updateMultiModelUI = updateMultiModelUI;
  }
  
  // Check if parts are overlapping and fix their positions
  function checkAndFixPartPositions() {
    if (!window.loadedModelParts || window.loadedModelParts.length <= 1) {
      return;
    }
    
    console.log('Checking part positions...');
    
    // Map of used positions to avoid overlap
    const usedPositions = new Map();
    const spacing = 100; // Base spacing between parts
    
    window.loadedModelParts.forEach((part, index) => {
      if (!part.model) return;
      
      // Ensure part is visible
      part.model.visible = true;
      
      // Get current position
      const pos = part.model.position;
      const posKey = `${Math.round(pos.x)}_${Math.round(pos.y)}_${Math.round(pos.z)}`;
      
      // Check if this position is already used
      if (usedPositions.has(posKey)) {
        console.log(`Part ${index} (${part.name}) overlaps with another part!`);
        
        // Move this part to a new position
        const offset = spacing * (usedPositions.get(posKey) + 1);
        part.model.position.x += offset;
        
        // Register this new position
        const newPosKey = `${Math.round(part.model.position.x)}_${Math.round(pos.y)}_${Math.round(pos.z)}`;
        usedPositions.set(newPosKey, 0);
      } else {
        // Register this position
        usedPositions.set(posKey, usedPositions.size);
      }
    });
    
    // Force render update
    if (window.renderer && window.scene && window.camera) {
      window.renderer.render(window.scene, window.camera);
    }
  }
  
  // Arrange models side by side
// Modified arrangeModels function
/**
 * Fixed arrangeModels function to ensure consistent height positioning
 * This version ensures all parts are placed directly on the grid (y=0)
 * regardless of their geometry after rotation
 */

function arrangeModels() {
  if (!window.loadedModelParts || window.loadedModelParts.length <= 1) {
    console.log('No models to arrange or only one model');
    return; // Nothing to arrange
  }
  
  console.log('Arranging models:', window.loadedModelParts.length);
  
  // Get the scene and camera from the window if available
  const scene = window.scene;
  const camera = window.camera;
  
  if (!scene) {
    console.warn('Scene not available for arranging models');
    return;
  }
  
  // Filter for unique visible parts
  const uniquePartsByName = new Map();
  
  // First pass to identify unique parts and ensure only one per name is shown
  window.loadedModelParts.forEach((part, index) => {
    if (!part || !part.name || !part.model) return;
    
    // Skip invisible parts in arrangement
    if (!part.model.visible) {
      return;
    }
    
    if (!uniquePartsByName.has(part.name)) {
      // This is the first part with this name - keep it
      uniquePartsByName.set(part.name, part);
    } else {
      // This is a duplicate - hide it
      part.model.visible = false;
      console.log(`Hiding duplicate part: ${part.name}`);
    }
  });
  
  // Get array of only visible parts for arrangement
  const visibleParts = Array.from(uniquePartsByName.values());
  console.log(`Arranging ${visibleParts.length} visible parts`);
  
  if (visibleParts.length === 0) {
    console.log('No visible parts to arrange');
    return;
  }
  
  // Calculate the best arrangement
  const count = visibleParts.length;
  
  // IMPROVEMENT: Increased spacing between models to prevent touching
  const spacing = 200; // Increased from 150 to ensure adequate separation
  
  // Determine layout grid (rows x columns)
  let cols = Math.ceil(Math.sqrt(count));
  let rows = Math.ceil(count / cols);
  
  console.log(`Arranging in ${rows}x${cols} grid with ${spacing}px spacing`);
  
  // Calculate total width and height
  const totalWidth = (cols - 1) * spacing;
  const totalHeight = (rows - 1) * spacing;
  
  // Track the minimum and maximum positions for camera adjustment
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
  
  // IMPROVEMENT: Calculate the actual model sizes to determine appropriate spacing
  // And check for proper collision prevention
  const modelSizes = [];
  
  // First calculate each model's size in world space
  visibleParts.forEach(part => {
    // Update the world matrix to account for rotations
    part.model.updateMatrixWorld(true);
    
    // Create a helper box for proper bounds calculation after rotation
    if (part.model.geometry) {
      if (!part.model.geometry.boundingBox) {
        part.model.geometry.computeBoundingBox();
      }
      
      // Get the bounding box of the geometry in local space
      const box = part.model.geometry.boundingBox.clone();
      
      // Create an array of 8 corner points from the bounding box
      const points = [
        new THREE.Vector3(box.min.x, box.min.y, box.min.z),
        new THREE.Vector3(box.min.x, box.min.y, box.max.z),
        new THREE.Vector3(box.min.x, box.max.y, box.min.z),
        new THREE.Vector3(box.min.x, box.max.y, box.max.z),
        new THREE.Vector3(box.max.x, box.min.y, box.min.z),
        new THREE.Vector3(box.max.x, box.min.y, box.max.z),
        new THREE.Vector3(box.max.x, box.max.y, box.min.z),
        new THREE.Vector3(box.max.x, box.max.y, box.max.z)
      ];
      
      // Transform all points to world space
      points.forEach(point => {
        point.applyMatrix4(part.model.matrixWorld);
      });
      
      // Find the lowest point (min Y) in world space after rotation
      const lowestPoint = Math.min(...points.map(p => p.y));
      
      // Store this lowest point on the part object for later use
      part.lowestPoint = lowestPoint;
      
      // IMPROVEMENT: Calculate the actual width and depth in world coordinates
      let minPartX = Math.min(...points.map(p => p.x));
      let maxPartX = Math.max(...points.map(p => p.x));
      let minPartZ = Math.min(...points.map(p => p.z));
      let maxPartZ = Math.max(...points.map(p => p.z));
      
      const size = {
        width: maxPartX - minPartX,
        depth: maxPartZ - minPartZ
      };
      
      // Store size information for spacing calculations
      modelSizes.push({
        part: part,
        size: size
      });
    }
  });
  
  // IMPROVEMENT: Find the maximum model dimension to use as minimum spacing
  const maxModelDimension = Math.max(...modelSizes.map(item => 
    Math.max(item.size.width, item.size.depth)
  ));
  
  // Ensure minimum spacing is at least 20% more than the largest model dimension
  const minSpacing = maxModelDimension * 1.2;
  const dynamicSpacing = Math.max(spacing, minSpacing);
  
  console.log(`Dynamically calculated spacing: ${dynamicSpacing}px based on largest model size: ${maxModelDimension}px`);
  
  // IMPROVEMENT: Position each model with dynamic spacing to ensure no overlap
  visibleParts.forEach((part, index) => {
    const model = part.model;
    
    // Double-check visibility
    if (!model.visible) {
      console.warn(`Part ${part.name} marked as invisible but in visible parts list`);
      return;
    }
    
    // Calculate grid position with dynamic spacing
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    // Calculate center-aligned position in XZ plane
    // Ensure we're using the dynamic spacing
    const xPos = col * dynamicSpacing - (totalWidth / 2) * (dynamicSpacing / spacing);
    const zPos = row * dynamicSpacing - (totalHeight / 2) * (dynamicSpacing / spacing);
    
    // Place the model directly on the grid by offsetting by its lowest point
    const yPos = part.lowestPoint !== undefined ? -part.lowestPoint : 0;
    
    // Position the model with a debug log
    console.log(`Positioning part ${index} (${part.name}) at (${xPos}, ${yPos}, ${zPos})`);
    model.position.set(xPos, yPos, zPos);
    
    // Force update of world matrix after position change
    model.updateMatrixWorld(true);
    
    // Update bounds for camera adjustment
    if (model.geometry && model.geometry.boundingBox) {
      // Get the actual bounds in world space
      const box = model.geometry.boundingBox.clone();
      const min = new THREE.Vector3(box.min.x, box.min.y, box.min.z);
      const max = new THREE.Vector3(box.max.x, box.max.y, box.max.z);
      
      // Transform to world space
      min.applyMatrix4(model.matrixWorld);
      max.applyMatrix4(model.matrixWorld);
      
      // Update min/max for camera adjustment
      minX = Math.min(minX, min.x);
      maxX = Math.max(maxX, max.x);
      minY = Math.min(minY, min.y);
      maxY = Math.max(maxY, max.y);
      minZ = Math.min(minZ, min.z);
      maxZ = Math.max(maxZ, max.z);
    }
  });
  
  // IMPROVEMENT: Final check to verify no parts are touching
  // This is a safety check that will adjust any problematic positions
  ensureNoPartsAreTouching(visibleParts, dynamicSpacing);
  
  // Update the camera to see all models
  if (camera) {
    if (minX !== Infinity && visibleParts.length > 0) {
      // We have valid bounds, position camera to see all parts
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2; 
      const centerZ = (minZ + maxZ) / 2;
      
      // Calculate distance needed to view all models
      const width = maxX - minX;
      const height = maxY - minY;
      const depth = maxZ - minZ;
      const maxDim = Math.max(width, height, depth);
      
      // Set camera position at an angle to the models
      // IMPROVEMENT: Increased distance factor for better viewing
      const distance = maxDim * 2.0; // Increased from 1.8 to ensure all models are visible
      camera.position.set(
        centerX + distance,
        centerY + distance / 2,
        centerZ + distance
      );
      camera.lookAt(centerX, centerY, centerZ);
      
      console.log(`Camera positioned at (${camera.position.x}, ${camera.position.y}, ${camera.position.z}) looking at (${centerX}, ${centerY}, ${centerZ})`);
    } else {
      // Fallback camera position or no visible parts
      camera.position.set(300, 200, 300);
      camera.lookAt(0, 0, 0);
    }
  }
  
  // Force a render if renderer is available
  if (window.renderer) {
    window.renderer.render(scene, camera);
  }
}

// IMPROVEMENT: New function to ensure no parts are touching
function ensureNoPartsAreTouching(parts, baseSpacing) {
  if (parts.length <= 1) return;
  
  console.log("Performing collision detection check between parts...");
  
  let adjustmentsMade = false;
  const safetyMargin = 10; // Additional buffer space in mm
  
  // Iterate through all pairs of parts
  for (let i = 0; i < parts.length; i++) {
    const partA = parts[i];
    if (!partA.model || !partA.model.visible || !partA.model.geometry) continue;
    
    for (let j = i + 1; j < parts.length; j++) {
      const partB = parts[j];
      if (!partB.model || !partB.model.visible || !partB.model.geometry) continue;
      
      // Get bounding boxes in world space
      const boxA = new THREE.Box3().setFromObject(partA.model);
      const boxB = new THREE.Box3().setFromObject(partB.model);
      
      // Check if boxes intersect or are too close
      if (boxA.intersectsBox(boxB) || areBoxesTooClose(boxA, boxB, safetyMargin)) {
        console.log(`Detected collision or proximity between ${partA.name} and ${partB.name}`);
        
        // Calculate vector between centers
        const centerA = new THREE.Vector3();
        boxA.getCenter(centerA);
        
        const centerB = new THREE.Vector3();
        boxB.getCenter(centerB);
        
        // Vector pointing from B to A
        const pushVector = new THREE.Vector3().subVectors(centerA, centerB);
        
        // Normalize and scale by half the base spacing plus part sizes
        const sizeA = boxA.getSize(new THREE.Vector3());
        const sizeB = boxB.getSize(new THREE.Vector3());
        
        const maxSizeA = Math.max(sizeA.x, sizeA.z);
        const maxSizeB = Math.max(sizeB.x, sizeB.z);
        
        const pushDistance = (maxSizeA/2 + maxSizeB/2 + baseSpacing/2 + safetyMargin) - pushVector.length();
        
        if (pushDistance > 0) {
          pushVector.normalize().multiplyScalar(pushDistance);
          
          // Move partA away from partB (only in X and Z directions)
          pushVector.y = 0; // Don't adjust height
          partA.model.position.add(pushVector);
          
          // Update world matrix
          partA.model.updateMatrixWorld(true);
          
          console.log(`Adjusted ${partA.name} position by (${pushVector.x.toFixed(2)}, 0, ${pushVector.z.toFixed(2)})`);
          adjustmentsMade = true;
        }
      }
    }
  }
  
  if (adjustmentsMade) {
    console.log("Made position adjustments to prevent parts from touching");
    
    // Re-center all parts if adjustments were made
    centerAllParts(parts);
  } else {
    console.log("All parts already adequately spaced");
  }
}

// IMPROVEMENT: New function to check if boxes are too close
function areBoxesTooClose(boxA, boxB, minDistance) {
  // Get centers
  const centerA = new THREE.Vector3();
  boxA.getCenter(centerA);
  
  const centerB = new THREE.Vector3();
  boxB.getCenter(centerB);
  
  // Get sizes
  const sizeA = boxA.getSize(new THREE.Vector3());
  const sizeB = boxB.getSize(new THREE.Vector3());
  
  // Check distance in X and Z directions (ignore Y)
  const dx = Math.abs(centerA.x - centerB.x) - (sizeA.x/2 + sizeB.x/2);
  const dz = Math.abs(centerA.z - centerB.z) - (sizeA.z/2 + sizeB.z/2);
  
  // If either distance is less than minDistance, they're too close
  return (dx < minDistance || dz < minDistance);
}

// IMPROVEMENT: New function to center all parts on the grid
function centerAllParts(parts) {
  if (parts.length === 0) return;
  
  // Find bounds of all parts
  const boundingBox = new THREE.Box3();
  
  parts.forEach(part => {
    if (part.model && part.model.visible) {
      boundingBox.expandByObject(part.model);
    }
  });
  
  // Calculate center of all parts
  const center = new THREE.Vector3();
  boundingBox.getCenter(center);
  
  // Only consider X and Z for centering (leave Y unchanged)
  const offsetX = -center.x;
  const offsetZ = -center.z;
  
  console.log(`Centering all parts by offset: (${offsetX.toFixed(2)}, 0, ${offsetZ.toFixed(2)})`);
  
  // Apply offset to all parts
  parts.forEach(part => {
    if (part.model && part.model.visible) {
      part.model.position.x += offsetX;
      part.model.position.z += offsetZ;
      part.model.updateMatrixWorld(true);
    }
  });
}
  
  // Reset view to show all models properly
  function resetModelView() {
    if (window.camera && window.scene && window.renderer) {
      // Reset camera position
      window.camera.position.set(300, 200, 300);
      window.camera.lookAt(0, 0, 0);
      
      // Render the scene
      window.renderer.render(window.scene, window.camera);
    }
  }
  
  // Update the multi-model controls UI
  function updateMultiModelUI() {
    const controlsContainer = document.getElementById('multi-model-controls');
    const modelPartsList = document.getElementById('model-parts-list');
    
    if (!controlsContainer || !modelPartsList) {
      return;
    }
    
    // Show/hide controls based on whether we have multiple parts
    if (window.loadedModelParts && window.loadedModelParts.length > 1) {
      controlsContainer.style.display = 'block';
      
      // Update the parts list
      modelPartsList.innerHTML = '';
      
      // Use a Set to track part names we've already added
      const addedPartNames = new Set();
      
      window.loadedModelParts.forEach((part, index) => {
        if (!part || !part.name) return;
        
        // Skip duplicate named parts in the UI
        if (addedPartNames.has(part.name)) return;
        addedPartNames.add(part.name);
        
        const partItem = document.createElement('div');
        partItem.style.marginBottom = '7px';
        partItem.style.display = 'flex';
        partItem.style.alignItems = 'center';
        
        // Get the color of this part
        const colorIndex = index % window.predefinedColors.length;
        const color = '#' + window.predefinedColors[colorIndex].toString(16).padStart(6, '0');
        
        // Check current visibility
        const isVisible = part.model && part.model.visible;
        
        partItem.innerHTML = `
          <div style="width: 12px; height: 12px; background-color: ${color}; margin-right: 7px; border-radius: 2px;"></div>
          <input type="checkbox" id="part-${index}" ${isVisible ? 'checked' : ''} style="margin-right: 5px;">
          <label for="part-${index}" style="font-size: 12px; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px;">${part.name || `Part ${index + 1}`}</label>
        `;
        
        // Add event listener to toggle part visibility
        const checkbox = partItem.querySelector(`#part-${index}`);
        checkbox.addEventListener('change', function() {
          // Find all parts with this name
          const matchingParts = window.loadedModelParts.filter(p => p.name === part.name);
          
          // Toggle visibility for all matching parts
          matchingParts.forEach(matchingPart => {
            if (matchingPart.model) {
              matchingPart.model.visible = this.checked;
            }
          });
          
          // Render the scene
          if (window.renderer && window.scene && window.camera) {
            window.renderer.render(window.scene, window.camera);
          }
          
          // IMPORTANT: Reposition parts after visibility change
          // This will rearrange all visible parts in a clean grid
          setTimeout(() => {
            console.log('Visibility changed - repositioning all parts');
            if (typeof window.arrangeModels === 'function') {
              window.arrangeModels();
            }
          }, 100);
        });
        
        modelPartsList.appendChild(partItem);
      });
    } else {
      controlsContainer.style.display = 'none';
    }
  }
  
  // Listen for model selection events from the library
  function listenForModelSelection() {
    // Method 1: Listen for custom events
    document.addEventListener('modelSelected', function(event) {
      if (event.detail && event.detail.id) {
        const model = event.detail;
        if (isThingiverseModel(model.id)) {
          handleThingiverseModel(model);
        }
      }
    });
    
    // Method 2: Override any existing selectModel function
    if (typeof window.selectModel === 'function') {
      const originalSelectModel = window.selectModel;
      
      window.selectModel = function(modelId) {
        // First check if this is a Thingiverse ID
        if (isThingiverseModel(modelId)) {
          const model = findModelById(modelId);
          if (model) {
            handleThingiverseModel(model);
            return;
          }
        }
        
        // Otherwise use original function
        originalSelectModel(modelId);
      };
    }
  }
  
  // Check if a model ID is a Thingiverse model
  function isThingiverseModel(modelId) {
    // Check if it's a numeric ID
    if (modelId && /^\d+$/.test(modelId)) {
      // Try to find the model and check its source
      const model = findModelById(modelId);
      return model && (model.source === 'Thingiverse' || !model.source);
    }
    return false;
  }
  
  // Add event listeners to model cards in the library
  function addModelCardListeners() {
    // Add mutation observer to watch for new model cards being added
    const modelGrid = document.getElementById('modelGrid');
    if (modelGrid) {
      const observer = new MutationObserver(function(mutations) {
        const selectButtons = document.querySelectorAll('.select-model-btn:not([data-thingiverse-handler])');
        selectButtons.forEach(button => {
          const card = button.closest('.model-card');
          if (card) {
            const modelId = card.getAttribute('data-model-id');
            if (isThingiverseModel(modelId)) {
              button.setAttribute('data-thingiverse-handler', 'true');
              button.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                // Get the model name from the card
                const nameEl = card.querySelector('.model-name');
                const modelName = nameEl ? nameEl.textContent : `Thingiverse Model #${modelId}`;
                
                // Show loading
                showLoading(`Importing ${modelName}...`);
                
                // Import the model directly - don't switch tabs
                importThingiverseModel(modelId, modelName);
              });
            }
          }
        });
      });
      
      observer.observe(modelGrid, { childList: true, subtree: true });
    }
  }
  
  // Handle a Thingiverse model selection
  function handleThingiverseModel(model) {
    console.log('Thingiverse model selected:', model);
    
    // Get the model ID and name
    const modelId = model.id;
    const modelName = model.name || `Thingiverse Model #${modelId}`;
    
    // Show loading
    showLoading(`Importing ${modelName}...`);
    
    // Import the model - don't switch tabs
    importThingiverseModel(modelId, modelName);
  }
  
  // Import a model from Thingiverse using direct methods
  function importThingiverseModel(thingId, modelName) {
    console.log(`Importing Thingiverse model #${thingId}`);
    
    // First ensure scene is initialized
    if (!ensureSceneExists()) {
      console.error('Failed to initialize Three.js scene - cannot import model');
      hideLoading();
      alert('Could not initialize 3D viewer. Please refresh the page and try again.');
      return;
    }
    
    // First get the model info to get a proper name if we don't have one
    if (!modelName) {
      fetchThingiverseModelInfo(thingId)
        .then(model => {
          importThingiverseModel(thingId, model.name || `Thingiverse Model #${thingId}`);
        })
        .catch(error => {
          console.error('Error fetching model info:', error);
          importThingiverseModel(thingId, `Thingiverse Model #${thingId}`);
        });
      return;
    }
    
    // Reset loaded model files array for new model
    loadedModelFiles = [];
    
    // Clear previous model parts if any
    if (window.loadedModelParts) {
      // Remove each part from the scene before clearing the array
      if (window.scene) {
        window.loadedModelParts.forEach(part => {
          if (part.model) {
            window.scene.remove(part.model);
          }
        });
      }
      window.loadedModelParts = [];
    }
    
    // Important: If there's a global 'model' variable, NULL it to prevent the scene from clearing
    if (window.model) {
      console.log('Setting window.model to null to prevent scene clearing');
      // Don't remove from scene yet, just set reference to null
      window.model = null;
    }
    
    // Add to pending downloads
    pendingDownloads[thingId] = {
      id: thingId,
      name: modelName,
      timestamp: Date.now()
    };
    
    // Update UI with model name
    updateUIForModel(modelName);
    
    // Try to fetch all STL files for this model
    fetchAllSTLFiles(thingId, modelName)
      .then(files => {
        if (files && files.length > 0) {
          console.log(`Found ${files.length} STL files for model #${thingId}`);
          return loadMultipleSTLFiles(files, modelName);
        } else {
          throw new Error('No STL files found for this model');
        }
      })
      .then(success => {
        if (success) {
          console.log('Successfully loaded all STL files');
          hideLoading();
          
          // Arrange models if we have multiple
          if (window.loadedModelParts && window.loadedModelParts.length > 1) {
            // Debug info
            console.log(`Loaded ${window.loadedModelParts.length} parts:`, 
              window.loadedModelParts.map(p => p.name));
            
            // Wait a moment for models to initialize in the scene
            setTimeout(() => {
              // IMPORTANT: Check if parts are visible and at different positions
              checkAndFixPartPositions();
              arrangeModels();
              updateMultiModelUI();
            }, 500);
          } else if (window.loadedModelParts && window.loadedModelParts.length === 1) {
            console.log('Only one part was loaded:', window.loadedModelParts[0].name);
          }
        } else {
          throw new Error('Failed to load STL files');
        }
      })
      .catch(error => {
        console.warn('Multi-file loading failed:', error.message);
        
        // Fall back to single file methods
        fetchDirectSTL(thingId, modelName)
          .then(stlData => {
            // We got the STL directly - create file and load it
            console.log('Successfully fetched STL data directly, size:', stlData.byteLength);
            createAndLoadSTLFile(stlData, modelName);
            hideLoading();
          })
          .catch(error => {
            console.warn('Direct STL fetch failed:', error.message);
            
            // Technique 2: Service Worker Proxy (if available)
            if ('serviceWorker' in navigator) {
              return useServiceWorkerProxy(thingId, modelName);
            } else {
              throw new Error('Service Worker not available');
            }
          })
          .then(success => {
            if (success) {
              console.log('Service worker proxy method succeeded');
              hideLoading();
            } else {
              throw new Error('Service worker proxy method failed');
            }
          })
          .catch(error => {
            console.warn('Service worker proxy failed:', error.message);
            
            // Technique 3: Web Worker import
            return useWebWorkerImport(thingId, modelName);
          })
          .then(success => {
            if (success) {
              console.log('Web worker import method succeeded');
              hideLoading();
            } else {
              throw new Error('Web worker import method failed');
            }
          })
          .catch(error => {
            console.warn('All automatic import methods failed:', error.message);
            
            // Final technique: Open download in background tab
            openBackgroundDownload(thingId, modelName);
          });
      });
  }
  
  // Fetch all STL files for a model
  function fetchAllSTLFiles(thingId, modelName) {
    return new Promise((resolve, reject) => {
      console.log('Fetching all STL files for model');
      
      // Get the files API endpoint
      fetch(`https://api.thingiverse.com/things/${thingId}/files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${ACCESS_TOKEN}`,
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Files API request failed: ${response.status}`);
        }
        return response.json();
      })
      .then(files => {
        if (!files || !Array.isArray(files) || files.length === 0) {
          throw new Error('No files found in API response');
        }
        
        // Find all STL files
        const stlFiles = files.filter(file => file.name.toLowerCase().endsWith('.stl'));
        
        if (stlFiles.length === 0) {
          throw new Error('No STL files found for this model');
        }
        
        console.log(`Found ${stlFiles.length} STL files:`, stlFiles.map(f => f.name));
        resolve(stlFiles);
      })
      .catch(error => {
        console.error('Error fetching STL files:', error);
        reject(error);
      });
    });
  }
  
  // Load multiple STL files
  function loadMultipleSTLFiles(files, modelName) {
    // Flag to track if we're currently loading
    if (isLoading) {
      console.warn('Already loading files - preventing duplicate load');
      return Promise.resolve(false);
    }
    
    // Set loading flag
    isLoading = true;
    
    return new Promise(async (resolve, reject) => {
      try {
        // Reset file tracking for a new model
        if (window.currentModelName !== modelName) {
          console.log(`New model "${modelName}" - clearing previous tracking`);
          alreadyLoadedUrls.clear();
          window.currentModelName = modelName;
        }
        
        // Track which parts we've already processed by name
        const processedNames = new Set();
        
        // Create global array for loaded parts if it doesn't exist
        if (!window.loadedModelParts) {
          window.loadedModelParts = [];
        } else {
          // Clear previous parts from scene
          if (window.scene) {
            window.loadedModelParts.forEach(part => {
              if (part.model) {
                window.scene.remove(part.model);
              }
            });
          }
          window.loadedModelParts = [];
        }
        
        // Disable any auto-clearing behavior in the original viewer code
        const originalHandleFileUpload = window.handleFileUpload;
        if (originalHandleFileUpload) {
          window.handleFileUpload = function() {
            console.log('Intercepted handleFileUpload to prevent scene clearing');
          };
        }
        
        // Show progress in loading message
        const loadingOverlay = document.getElementById('loading');
        const loadingText = loadingOverlay ? loadingOverlay.querySelector('.loading-text') : null;
        
        // Filter files to unique set by URL
        const uniqueFiles = [];
        for (const file of files) {
          if (!file || !file.download_url) continue;
          
          // Skip if already loaded
          if (alreadyLoadedUrls.has(file.download_url)) {
            console.log(`Skipping duplicate file: ${file.name} (already loaded)`);
            continue;
          }
          
          // Add to unique files
          uniqueFiles.push(file);
        }
        
        console.log(`Found ${uniqueFiles.length} new files to process out of ${files.length} total`);
        
        // Process unique files sequentially
        for (let i = 0; i < uniqueFiles.length; i++) {
          const file = uniqueFiles[i];
          
          // Mark as loaded before fetching to prevent race conditions
          alreadyLoadedUrls.set(file.download_url, file.name);
          
          // Create a meaningful part name
          const partName = file.name.replace(/\.stl$/i, '').replace(/_/g, ' ');
          
          // Skip if we've already processed a part with this name
          if (processedNames.has(partName)) {
            console.log(`Skipping duplicate part: ${partName}`);
            continue;
          }
          
          // Mark as processed
          processedNames.add(partName);
          
          // Update loading message
          if (loadingText) {
            loadingText.textContent = `Loading part ${i+1}/${uniqueFiles.length}: ${file.name}`;
          }
          
          try {
            // Fetch the STL file
            const response = await fetch(file.download_url, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${ACCESS_TOKEN}`
              }
            });
            
            if (!response.ok) {
              console.warn(`Failed to fetch ${file.name}: ${response.status}`);
              // Remove from loaded urls since it failed
              alreadyLoadedUrls.delete(file.download_url);
              continue; // Skip this file but continue with others
            }
            
            const stlData = await response.arrayBuffer();
            
            // Load this part
            console.log(`Loading part ${i+1}: ${partName}`);
            const partModel = await loadSTLPart(stlData, partName, i);
            
            if (partModel) {
              // Store loaded file info
              loadedModelFiles.push({
                name: file.name,
                data: stlData
              });
              
              // Store in global parts array
              window.loadedModelParts.push({
                name: partName,
                model: partModel
              });
              
              // Force immediate position update to prevent overlap
              partModel.position.x = i * 120;
              
              // Force a render to update the view
              if (window.renderer && window.scene && window.camera) {
                window.renderer.render(window.scene, window.camera);
              }
            }
          } catch (error) {
            console.warn(`Failed to load part ${file.name}:`, error);
            // Remove from loaded urls since it failed
            alreadyLoadedUrls.delete(file.download_url);
          }
        }
        
        // Restore original file handler
        if (originalHandleFileUpload) {
          window.handleFileUpload = originalHandleFileUpload;
        }
        
        // Clear loading flag
        isLoading = false;
        
        // If we loaded at least one part, consider it a success
        if (window.loadedModelParts.length > 0) {
          // Show success notification
          showSuccessNotification(`${modelName} (${window.loadedModelParts.length} parts)`);
          resolve(true);
        } else {
          reject(new Error('Failed to load any parts'));
        }
      } catch (error) {
        console.error('Error loading multiple STL files:', error);
        // Clear loading flag on error
        isLoading = false;
        reject(error);
      }
    });
  }
  
// Enhanced function to load a single STL part with improved initial positioning
function loadSTLPart(stlData, partName, index) {
  return new Promise((resolve, reject) => {
    try {
      // First ensure the scene exists
      if (!ensureSceneExists()) {
        reject(new Error('THREE.js scene not available and could not be created'));
        return;
      }
      
      console.log(`Processing part ${index+1}: ${partName}`);
      
      // Create a unique colored material for this part
      const colorIndex = index % window.predefinedColors.length;
      const material = new THREE.MeshStandardMaterial({
        color: window.predefinedColors[colorIndex],
        metalness: 0.1,
        roughness: 0.7
      });
      
      let geometry;
      
      // Try multiple methods to parse the STL data
      try {
        // Method 1: Try to use existing parseSTL function if available
        if (typeof window.parseSTL === 'function') {
          console.log('Using window.parseSTL function');
          geometry = window.parseSTL(stlData);
        } 
        // Method 2: Try Three.js STLLoader if available
        else if (THREE.STLLoader) {
          console.log('Using THREE.STLLoader');
          const loader = new THREE.STLLoader();
          geometry = loader.parse(stlData);
        } 
        // Method 3: Manual STL parsing as fallback
        else {
          console.log('Using manual STL parsing');
          geometry = parseSTLBinary(stlData);
        }
      } catch (parseError) {
        console.error('Error parsing STL data:', parseError);
        // Last resort attempt with our own parser
        try {
          geometry = parseSTLBinary(stlData);
        } catch (fallbackError) {
          reject(new Error('Failed to parse STL data with all methods'));
          return;
        }
      }
      
      if (!geometry) {
        reject(new Error('Failed to create geometry'));
        return;
      }
      
      // Compute bounding box and center
      geometry.computeBoundingBox();
      
      // Create mesh with the geometry
      const mesh = new THREE.Mesh(geometry, material);
      
      // Apply correct rotation to fix orientation
      mesh.rotation.x = -Math.PI / 2; // Rotate -90 degrees around X axis
      
      // IMPROVEMENT: Set an initial position that ensures parts don't overlap
      // Calculate a wider spacing based on the number of parts we expect to load
      // Scale spacing based on part size to ensure no overlaps
      const initialSpacing = 180; // Increased from 120 for better initial separation
      
      // Get part dimensions (after rotation)
      mesh.updateMatrixWorld(true);
      let size = new THREE.Vector3();
      geometry.boundingBox.getSize(size);
      
      // Scale by maximum dimension to avoid overlaps
      const maxDimension = Math.max(size.x, size.y, size.z);
      const scaleFactor = Math.max(1, maxDimension / 100); // Normalize based on 100mm reference size
      
      // Choose a spiral pattern for initial placement to better distribute parts
      const initialOffset = calculateSpiralOffset(index, initialSpacing * scaleFactor);
      
      // Position the part with the spiral pattern to avoid initial overlap
      mesh.position.x = initialOffset.x;
      mesh.position.y = 0; // Always place on the grid
      mesh.position.z = initialOffset.z;
      
      // Store mesh dimensions for debugging
      const dimensions = {};
      if (geometry.boundingBox) {
        const box = geometry.boundingBox;
        dimensions.width = box.max.x - box.min.x;
        dimensions.height = box.max.y - box.min.y;
        dimensions.depth = box.max.z - box.min.z;
      }
      
      console.log(`Part ${index+1} dimensions:`, dimensions);
      console.log(`Initial position: (${mesh.position.x}, ${mesh.position.y}, ${mesh.position.z})`);
      
      // Add to scene
      window.scene.add(mesh);
      
      // Force a render to show this part
      if (window.renderer && window.camera) {
        window.renderer.render(window.scene, window.camera);
      }
      
      resolve(mesh);
    } catch (error) {
      console.error('Error loading STL part:', error);
      reject(error);
    }
  });
}

// Helper function to calculate a spiral pattern position based on index
function calculateSpiralOffset(index, spacing) {
  // Use a spiral pattern for more efficient space usage
  // This distributes parts in a spiral pattern outward from the center
  const angle = index * 0.5; // Angle increment (in radians)
  const radius = Math.sqrt(index) * spacing * 0.6; // Spiral radius grows with square root of index
  
  return {
    x: Math.cos(angle) * radius,
    z: Math.sin(angle) * radius
  };
}

// Enhanced function to check if parts are overlapping and fix their positions
function checkAndFixPartPositions() {
  if (!window.loadedModelParts || window.loadedModelParts.length <= 1) {
    return;
  }
  
  console.log('Checking part positions...');
  
  // Gather all visible parts
  const visibleParts = window.loadedModelParts.filter(part => 
    part && part.model && part.model.visible
  );
  
  // Do nothing if no visible parts
  if (visibleParts.length === 0) {
    console.log('No visible parts to position');
    return;
  }
  
  // If only one part, center it
  if (visibleParts.length === 1) {
    visibleParts[0].model.position.set(0, 0, 0);
    console.log('Only one part - centered at origin');
    return;
  }
  
  // Find the overall bounding box of all visible parts
  const combinedBox = new THREE.Box3();
  visibleParts.forEach(part => {
    combinedBox.expandByObject(part.model);
  });
  
  // Calculate min separation distance based on part sizes
  const sizes = visibleParts.map(part => {
    const box = new THREE.Box3().setFromObject(part.model);
    const size = new THREE.Vector3();
    box.getSize(size);
    return Math.max(size.x, size.z); // We care about x and z (horizontal plane)
  });
  
  // Use maximum part size as basis for minimum separation
  const maxPartSize = Math.max(...sizes);
  const minSeparation = maxPartSize * 1.2; // 20% more than largest part
  
  console.log(`Min separation distance based on part sizes: ${minSeparation}`);
  
  // Create a grid arrangement
  const count = visibleParts.length;
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  
  // Calculate proper spacing
  const spacing = Math.max(150, minSeparation);
  
  // Center offset for the grid
  const gridWidth = (cols - 1) * spacing;
  const gridDepth = (rows - 1) * spacing;
  const centerX = -gridWidth / 2;
  const centerZ = -gridDepth / 2;
  
  // Position each part in the grid
  visibleParts.forEach((part, index) => {
    // Skip parts with no model
    if (!part.model) return;
    
    // Calculate grid position
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    // Calculate position within grid
    const xPos = centerX + col * spacing;
    const zPos = centerZ + row * spacing;
    
    // Set y position to put the bottom of the part on the grid
    let yPos = 0;
    if (part.lowestPoint !== undefined) {
      yPos = -part.lowestPoint;
    } else {
      // Compute lowest point if not already calculated
      const box = new THREE.Box3().setFromObject(part.model);
      yPos = -box.min.y;
    }
    
    // Position the part
    part.model.position.set(xPos, yPos, zPos);
    
    console.log(`Positioned part ${index} (${part.name}) at (${xPos}, ${yPos}, ${zPos})`);
  });
  
  // Perform collision detection and resolution as a final check
  resolveCollisions(visibleParts, spacing);
  
  // Force render update
  if (window.renderer && window.scene && window.camera) {
    window.renderer.render(window.scene, window.camera);
  }
}

// Helper function to detect and resolve collisions between parts
function resolveCollisions(parts, baseSpacing) {
  // Perform multiple passes for complex arrangements
  const MAX_PASSES = 3;
  let movesInLastPass = 0;
  
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    movesInLastPass = 0;
    
    for (let i = 0; i < parts.length; i++) {
      const partA = parts[i];
      if (!partA.model) continue;
      
      for (let j = i + 1; j < parts.length; j++) {
        const partB = parts[j];
        if (!partB.model) continue;
        
        // Create bounding boxes
        const boxA = new THREE.Box3().setFromObject(partA.model);
        const boxB = new THREE.Box3().setFromObject(partB.model);
        
        // Check for intersection and minimum separation
        const MIN_SEPARATION = baseSpacing * 0.5; // Half the base spacing as minimum
        let needsAdjustment = false;
        
        // Calculate centers
        const centerA = new THREE.Vector3();
        boxA.getCenter(centerA);
        
        const centerB = new THREE.Vector3();
        boxB.getCenter(centerB);
        
        // Calculate sizes
        const sizeA = new THREE.Vector3();
        boxA.getSize(sizeA);
        
        const sizeB = new THREE.Vector3();
        boxB.getSize(sizeB);
        
        // Calculate current separation
        const dx = Math.abs(centerA.x - centerB.x) - (sizeA.x/2 + sizeB.x/2);
        const dz = Math.abs(centerA.z - centerB.z) - (sizeA.z/2 + sizeB.z/2);
        
        // Need adjustment if there's intersection or insufficient separation
        needsAdjustment = (dx < MIN_SEPARATION || dz < MIN_SEPARATION);
        
        if (needsAdjustment) {
          // Calculate push vector (in XZ plane only)
          const pushVec = new THREE.Vector3(
            centerA.x - centerB.x,
            0,
            centerA.z - centerB.z
          );
          
          // Normalize and scale
          if (pushVec.length() < 0.001) {
            // If centers are too close, push in random direction
            const angle = Math.random() * Math.PI * 2;
            pushVec.set(Math.cos(angle), 0, Math.sin(angle));
          } else {
            pushVec.normalize();
          }
          
          // Calculate push distance to achieve minimum separation
          const currentDistance = new THREE.Vector2(
            Math.abs(centerA.x - centerB.x),
            Math.abs(centerA.z - centerB.z)
          ).length();
          
          const requiredDistance = MIN_SEPARATION + (sizeA.x/2 + sizeB.x/2 + sizeA.z/2 + sizeB.z/2) / 2;
          const pushDistance = Math.max(0, requiredDistance - currentDistance);
          
          // Apply push (distribute movement between both parts)
          if (pushDistance > 1) { // Only move if significant adjustment needed
            pushVec.multiplyScalar(pushDistance / 2); // Split adjustment between parts
            
            partA.model.position.add(pushVec);
            partB.model.position.sub(pushVec);
            
            // Record that we made a move
            movesInLastPass++;
            
            console.log(`Pass ${pass+1}: Adjusted positions of ${partA.name} and ${partB.name}`);
          }
        }
      }
    }
    
    // If no moves were made in this pass, we're done
    if (movesInLastPass === 0) {
      console.log(`Collision resolution complete after ${pass+1} passes`);
      break;
    }
  }
  
  // Ensure everything is centered after resolving collisions
  if (parts.length > 0) {
    // Find current bounds
    const bounds = new THREE.Box3();
    parts.forEach(part => {
      if (part.model) {
        bounds.expandByObject(part.model);
      }
    });
    
    // Calculate center offset
    const center = new THREE.Vector3();
    bounds.getCenter(center);
    center.y = 0; // Don't adjust height
    
    // Apply center offset to all parts
    if (Math.abs(center.x) > 0.1 || Math.abs(center.z) > 0.1) {
      console.log(`Centering all parts by offset: (${-center.x}, 0, ${-center.z})`);
      
      parts.forEach(part => {
        if (part.model) {
          part.model.position.x -= center.x;
          part.model.position.z -= center.z;
        }
      });
    }
  }
}
  
  // Function to parse binary STL
  function parseSTLBinary(data) {
    try {
      const reader = new DataView(data);
      
      // Skip the header
      let offset = 80;
      
      // Read number of triangles
      const faces = reader.getUint32(offset, true);
      offset += 4;
      
      if (faces < 1 || faces > 5000000) {
        // Sanity check - unreasonable number of faces
        throw new Error(`Invalid number of faces: ${faces}`);
      }
      
      const positions = [];
      const normals = [];
      
      for (let face = 0; face < faces; face++) {
        // Get normal
        const nx = reader.getFloat32(offset, true);
        const ny = reader.getFloat32(offset + 4, true);
        const nz = reader.getFloat32(offset + 8, true);
        offset += 12;
        
        // Get vertices
        for (let i = 0; i < 3; i++) {
          const x = reader.getFloat32(offset, true);
          const y = reader.getFloat32(offset + 4, true);
          const z = reader.getFloat32(offset + 8, true);
          offset += 12;
          
          positions.push(x, y, z);
          normals.push(nx, ny, nz);
        }
        
        // Skip attribute byte count
        offset += 2;
        
        // Check for buffer overrun
        if (offset > data.byteLength) {
          throw new Error('STL file data is corrupted - unexpected end of file');
        }
      }
      
      // Create geometry
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      
      return geometry;
    } catch (error) {
      console.error('Error in parseSTLBinary:', error);
      throw error;
    }
  }
  
  // Try to fetch STL directly with proper CORS headers
  function fetchDirectSTL(thingId, modelName) {
    return new Promise((resolve, reject) => {
      console.log('Attempting direct STL fetch');
      
      // First try to get the files API endpoint
      fetch(`https://api.thingiverse.com/things/${thingId}/files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${ACCESS_TOKEN}`,
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Files API request failed: ${response.status}`);
        }
        return response.json();
      })
      .then(files => {
        if (!files || !Array.isArray(files) || files.length === 0) {
          throw new Error('No files found in API response');
        }
        
        // Find the first STL file
        const stlFile = files.find(file => file.name.toLowerCase().endsWith('.stl'));
        
        if (!stlFile || !stlFile.download_url) {
          throw new Error('No STL files found for this model');
        }
        
        console.log('Found STL file:', stlFile.name);
        
        // Fetch the STL file with authorization
        return fetch(stlFile.download_url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${ACCESS_TOKEN}`
          }
        });
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`STL download failed: ${response.status}`);
        }
        return response.arrayBuffer();
      })
      .then(stlData => {
        resolve(stlData);
      })
      .catch(error => {
        console.error('Error in fetchDirectSTL:', error);
        reject(error);
      });
    });
  }
  
  // Try to use a service worker as a proxy (if available)
  function useServiceWorkerProxy(thingId, modelName) {
    return new Promise((resolve, reject) => {
      console.log('Attempting service worker proxy method');
      
      // Check if we already have a service worker
      if (navigator.serviceWorker.controller) {
        // Send message to service worker to fetch the STL
        navigator.serviceWorker.controller.postMessage({
          type: 'fetchSTL',
          thingId: thingId,
          token: ACCESS_TOKEN
        });
        
        // Listen for response from service worker
        navigator.serviceWorker.addEventListener('message', function(event) {
          if (event.data && event.data.type === 'stlData' && event.data.thingId === thingId) {
            if (event.data.error) {
              reject(new Error(event.data.error));
            } else {
              // Create and load the STL file
              createAndLoadSTLFile(event.data.data, modelName);
              resolve(true);
            }
          }
        });
        
        // Set a timeout
        setTimeout(() => {
          reject(new Error('Service worker proxy timed out'));
        }, 10000);
      } else {
        // Register a service worker if possible
        navigator.serviceWorker.register('/thingiverse-proxy-sw.js')
          .then(registration => {
            console.log('Service Worker registered:', registration);
            // We need to wait for the service worker to activate
            reject(new Error('Service worker registered but not activated yet - try again'));
          })
          .catch(error => {
            console.error('Service Worker registration failed:', error);
            reject(error);
          });
      }
    });
  }
  
  // Try to use a web worker for import
  function useWebWorkerImport(thingId, modelName) {
    return new Promise((resolve, reject) => {
      console.log('Attempting web worker import method');
      
      try {
        // Create a web worker
        const workerBlob = new Blob([`
          self.addEventListener('message', function(e) {
            const thingId = e.data.thingId;
            const token = e.data.token;
            
            // First try to get the files API endpoint
            fetch('https://api.thingiverse.com/things/' + thingId + '/files', {
              method: 'GET',
              headers: {
                'Authorization': 'Bearer ' + token,
                'Accept': 'application/json'
              }
            })
            .then(response => response.json())
            .then(files => {
              if (!files || !Array.isArray(files) || files.length === 0) {
                throw new Error('No files found');
              }
              
              // Find an STL file
              const stlFile = files.find(file => file.name.toLowerCase().endsWith('.stl'));
              
              if (!stlFile) {
                throw new Error('No STL file found');
              }
              
              // Fetch the STL file
              return fetch(stlFile.download_url, {
                method: 'GET',
                headers: {
                  'Authorization': 'Bearer ' + token
                }
              });
            })
            .then(response => response.arrayBuffer())
            .then(data => {
              self.postMessage({ 
                type: 'stlData', 
                thingId: thingId,
                filename: 'model.stl',
                data: data 
              });
            })
            .catch(error => {
              self.postMessage({ 
                type: 'error', 
                thingId: thingId,
                message: error.message 
              });
            });
          });
        `], { type: 'application/javascript' });
        
        const worker = new Worker(URL.createObjectURL(workerBlob));
        
        // Listen for messages from the worker
        worker.addEventListener('message', function(e) {
          if (e.data.type === 'stlData' && e.data.thingId === thingId) {
            // Create and load the STL file
            createAndLoadSTLFile(e.data.data, modelName);
            worker.terminate();
            resolve(true);
          } else if (e.data.type === 'error' && e.data.thingId === thingId) {
            worker.terminate();
            reject(new Error(e.data.message));
          }
        });
        
        // Send message to the worker
        worker.postMessage({
          thingId: thingId,
          token: ACCESS_TOKEN
        });
        
        // Set a timeout
        setTimeout(() => {
          worker.terminate();
          reject(new Error('Web worker import timed out'));
        }, 15000);
      } catch (error) {
        console.error('Error creating web worker:', error);
        reject(error);
      }
    });
  }
  
  // Open a background download and try to detect when it completes
  function openBackgroundDownload(thingId, modelName) {
    console.log('Using background download method');
    
    // Create a minimal UI to inform the user what's happening
    const statusBar = document.createElement('div');
    statusBar.id = 'thingiverse-status';
    statusBar.style.position = 'fixed';
    statusBar.style.bottom = '20px';
    statusBar.style.left = '50%';
    statusBar.style.transform = 'translateX(-50%)';
    statusBar.style.background = '#2196F3';
    statusBar.style.color = 'white';
    statusBar.style.padding = '10px 20px';
    statusBar.style.borderRadius = '4px';
    statusBar.style.zIndex = '9999';
    statusBar.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    statusBar.style.display = 'flex';
    statusBar.style.alignItems = 'center';
    statusBar.style.gap = '10px';
    
    statusBar.innerHTML = `
      <div style="animation: thingiverse-spin 1s linear infinite; width: 20px; height: 20px; border: 2px solid white; border-top-color: transparent; border-radius: 50%;"></div>
      <div>Downloading ${modelName}... Click "Upload STL" when download completes</div>
      <button id="thingiverse-upload-btn" style="margin-left: 15px; background: white; color: #2196F3; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">Upload STL</button>
    `;
    
    // Add the spinning animation
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes thingiverse-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(statusBar);
    
    // Add event listener to upload button
    document.getElementById('thingiverse-upload-btn').addEventListener('click', function() {
      // Click the file input to trigger the file selection dialog
      const fileInput = document.getElementById('file-input');
      if (fileInput) {
        fileInput.click();
      }
    });
    
    // Open the download in a background tab (or new window if needed)
    // First try the direct download link for an STL
    const downloadWindow = window.open(`https://www.thingiverse.com/download:${thingId}`, '_blank');
    
    // Set a timer to check if the download initiated
    setTimeout(() => {
      // Check if the window was blocked
      if (!downloadWindow || downloadWindow.closed) {
        console.warn('Download window was blocked or closed');
        
        // Update status bar
        statusBar.innerHTML = `
          <div style="color: #FFC107;">⚠️</div>
          <div>Download popup blocked. <a href="https://www.thingiverse.com/thing:${thingId}/zip" target="_blank" style="color: white; text-decoration: underline;">Click here</a> to download, then click "Upload STL"</div>
          <button id="thingiverse-upload-btn" style="margin-left: 15px; background: white; color: #2196F3; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">Upload STL</button>
        `;
        
        // Re-add event listener
        document.getElementById('thingiverse-upload-btn').addEventListener('click', function() {
          const fileInput = document.getElementById('file-input');
          if (fileInput) {
            fileInput.click();
          }
        });
      }
    }, 1000);
    
    // Automatically hide the status bar after 30 seconds
    setTimeout(() => {
      if (document.body.contains(statusBar)) {
        statusBar.style.opacity = '0';
        statusBar.style.transition = 'opacity 0.5s ease';
        
        setTimeout(() => {
          if (document.body.contains(statusBar)) {
            statusBar.remove();
          }
        }, 500);
      }
    }, 30000);
    
    // Listen for file uploads (if the user selects the file we just downloaded)
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      const originalOnChange = fileInput.onchange;
      
      fileInput.onchange = function(e) {
        // Remove the status bar
        if (document.body.contains(statusBar)) {
          statusBar.remove();
        }
        
        // Update the model name
        if (e.target.files && e.target.files.length > 0) {
          updateUIForModel(modelName);
        }
        
        // Call the original onchange handler if it exists
        if (typeof originalOnChange === 'function') {
          originalOnChange(e);
        }
      };
    }
    
    // Hide the loading overlay
    hideLoading();
  }
  
  // Create a File object from STL data and load it into the viewer
  function createAndLoadSTLFile(stlData, modelName) {
    // Clean up filename for use as a file name
    const cleanName = modelName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fileName = `${cleanName}.stl`;
    
    // Create a File object from the ArrayBuffer
    const stlFile = new File([stlData], fileName, { type: 'model/stl' });
    
    // Update the model name in the UI
    updateUIForModel(modelName);
    
    // Use the existing file handler function if available
    if (typeof handleFileUpload === 'function') {
      console.log('Using handleFileUpload function');
      handleFileUpload(stlFile);
    } else if (window.handleFileUpload) {
      console.log('Using window.handleFileUpload function');
      window.handleFileUpload(stlFile);
    } else {
      console.log('Using file input change event');
      // Last resort: create a fake file input change event
      const fileInput = document.getElementById('file-input');
      if (fileInput) {
        // Create a DataTransfer object
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(stlFile);
        fileInput.files = dataTransfer.files;
        
        // Trigger the change event
        const event = new Event('change', { bubbles: true });
        fileInput.dispatchEvent(event);
      } else {
        console.error('Could not find file input element');
        alert('Could not load model automatically. Please download and upload manually.');
      }
    }
    
    // Show success notification
    showSuccessNotification(modelName);
  }
  
  // Fetch model information from Thingiverse API
  function fetchThingiverseModelInfo(thingId) {
    return new Promise((resolve, reject) => {
      fetch(`https://api.thingiverse.com/things/${thingId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${ACCESS_TOKEN}`,
          'Accept': 'application/json'
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        resolve(data);
      })
      .catch(error => {
        reject(error);
      });
    });
  }
  
  // Show success notification
  function showSuccessNotification(modelName) {
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.bottom = '20px';
    notification.style.right = '20px';
    notification.style.background = '#4CAF50';
    notification.style.color = 'white';
    notification.style.padding = '15px 20px';
    notification.style.borderRadius = '4px';
    notification.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    notification.style.zIndex = '9999';
    notification.style.display = 'flex';
    notification.style.alignItems = 'center';
    notification.style.gap = '10px';
    notification.style.maxWidth = '400px';
    
    notification.innerHTML = `
      <div style="font-size: 20px;">✓</div>
      <div>
        <div style="font-weight: 600; margin-bottom: 3px;">${modelName}</div>
        <div>Successfully imported from Thingiverse!</div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transition = 'opacity 0.5s ease';
      
      setTimeout(() => {
        if (document.body.contains(notification)) {
          notification.remove();
        }
      }, 500);
    }, 5000);
  }
  
  // Set up file detection for downloads
  function setupFileDetection() {
    // This is tricky due to browser security restrictions
    // We can listen for focus events which might indicate a download completed
    window.addEventListener('focus', function() {
      // Check if we have any pending downloads older than 2 seconds
      const now = Date.now();
      const pendingIds = Object.keys(pendingDownloads);
      
      for (const id of pendingIds) {
        const download = pendingDownloads[id];
        if (now - download.timestamp > 2000) {
          // This download has been pending for a while
          // We'll create a notification to help the user
          createDownloadNotification(download.id, download.name);
          
          // Remove from pending to avoid duplicate notifications
          delete pendingDownloads[id];
        }
      }
    });
  }
  

  
  // Show loading message
  function showLoading(message) {
    const loadingOverlay = document.getElementById('loading');
    if (loadingOverlay) {
      const loadingText = loadingOverlay.querySelector('.loading-text');
      if (loadingText) {
        loadingText.textContent = message;
      }
      loadingOverlay.classList.add('active');
    } else {
      // Create loading overlay if it doesn't exist
      const overlay = document.createElement('div');
      overlay.id = 'loading';
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.backgroundColor = 'rgba(0,0,0,0.5)';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.zIndex = '9999';
      overlay.classList.add('active');
      
      const spinner = document.createElement('div');
      spinner.style.border = '5px solid #f3f3f3';
      spinner.style.borderRadius = '50%';
      spinner.style.borderTop = '5px solid #3498db';
      spinner.style.width = '40px';
      spinner.style.height = '40px';
      spinner.style.animation = 'spin 2s linear infinite';
      
      const loadingText = document.createElement('div');
      loadingText.className = 'loading-text';
      loadingText.textContent = message;
      loadingText.style.color = 'white';
      loadingText.style.marginLeft = '15px';
      loadingText.style.fontWeight = 'bold';
      
      const container = document.createElement('div');
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.appendChild(spinner);
      container.appendChild(loadingText);
      
      overlay.appendChild(container);
      
      // Add spin animation
      const style = document.createElement('style');
      style.innerHTML = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
      
      document.body.appendChild(overlay);
    }
  }
  
  // Hide loading message
  function hideLoading() {
    const loadingOverlay = document.getElementById('loading');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('active');
    }
  }
  
  // Update UI elements for the loaded model
  function updateUIForModel(modelName) {
    // Update model name display
    const modelNameEl = document.getElementById('model-name');
    if (modelNameEl) {
      modelNameEl.textContent = modelName;
    }
    
    // Update model properties form field
    const modelProp = document.getElementById('model-prop');
    if (modelProp) {
      modelProp.value = modelName;
    }
  }
  
  // Helper function to find a model by ID
  function findModelById(modelId) {
    // Try to find model in the filtered or all models arrays
    if (window.filteredModels && Array.isArray(window.filteredModels)) {
      const model = window.filteredModels.find(m => m.id == modelId);
      if (model) return model;
    }
    
    if (window.allModels && Array.isArray(window.allModels)) {
      const model = window.allModels.find(m => m.id == modelId);
      if (model) return model;
    }
    
    // Default fallback model object
    return {
      id: modelId,
      name: `Thingiverse Model #${modelId}`,
      source: 'Thingiverse'
    };
  }
  
  // Initialize the integration
  initialize();
  
  // Also try again after a short delay to ensure all elements are loaded
  setTimeout(initialize, 1000);
  
  // Add a listener for tab changes, as the model library tab might be loaded later
  const sourceTabs = document.querySelectorAll('.source-tab');
  sourceTabs.forEach(tab => {
    tab.addEventListener('click', function() {
      if (this.getAttribute('data-source') === 'library') {
        // Re-initialize when switching to library tab
        setTimeout(initialize, 500);
      }
    });
  });
  
  // Retry Three.js initialization periodically if needed
  const initInterval = setInterval(function() {
    if (initialized || initializationAttempts >= MAX_ATTEMPTS) {
      clearInterval(initInterval);
      return;
    }
    
    initializeThreeJsEnvironment();
  }, 500);
  
  // Expose key functions to global scope
  window.importThingiverseModel = importThingiverseModel;
  window.arrangeModels = arrangeModels;
  window.checkAndFixPartPositions = checkAndFixPartPositions;
  window.ensureSceneExists = ensureSceneExists;
  window.getScene = getScene;
  window.parseSTLBinary = parseSTLBinary;
  
  console.log('Thingiverse Multi-File Importer with Three.js Integration Fix - Setup complete');
});

/**
 * Multi-Part Volume and Surface Aggregator for 3D Printing Calculator
 * This script enhances the calculator by combining values from multiple model parts
 */

document.addEventListener('DOMContentLoaded', function() {
  console.log('Multi-Part Calculator loaded');
  
  // Function to update the combined volume and surface area display
  function updateCombinedModelStats() {
    // If there are no multiple parts loaded, do nothing
    if (!window.loadedModelParts || window.loadedModelParts.length <= 1) {
      return;
    }
    
    console.log(`Calculating combined stats for ${window.loadedModelParts.length} parts`);
    
    // Initialize total values
    let totalVolume = 0;
    let totalSurfaceArea = 0;
    let visiblePartsCount = 0;
    
    // Calculate the total volume and surface area of all visible parts
    window.loadedModelParts.forEach(part => {
      // Skip parts that are not visible
      if (!part.model || !part.model.visible) {
        return;
      }
      
      visiblePartsCount++;
      
      // Get the geometry of the part
      const geometry = part.model.geometry;
      if (!geometry) {
        return;
      }
      
      // Make sure bounding box is computed
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }
      
      // Calculate the volume
      const box = geometry.boundingBox;
      if (box) {
        // Get dimensions in mm
        const width = Math.abs(box.max.x - box.min.x);
        const height = Math.abs(box.max.y - box.min.y);
        const depth = Math.abs(box.max.z - box.min.z);
        
        // For simple box-based volume approximation (in cm³)
        const volumeApprox = (width * height * depth) / 1000;
        
        // For simple surface area approximation (in cm²)
        const surfaceApprox = 2 * ((width * height) + (width * depth) + (height * depth)) / 100;
        
        // Try to get more accurate volume if the geometry has a detailed volume method
        if (typeof geometry.getVolume === 'function') {
          const accurateVolume = geometry.getVolume() / 1000; // Convert to cm³
          totalVolume += accurateVolume;
        } else {
          totalVolume += volumeApprox;
        }
        
        // Try to get more accurate surface area if the geometry has a detailed surface area method
        if (typeof geometry.getSurfaceArea === 'function') {
          const accurateSurface = geometry.getSurfaceArea() / 100; // Convert to cm²
          totalSurfaceArea += accurateSurface;
        } else {
          totalSurfaceArea += surfaceApprox;
        }
      }
    });
    
    // Update the display if we have visible parts
    if (visiblePartsCount > 0) {
      // Update volume display
      const volumeEl = document.getElementById('volume');
      if (volumeEl) {
        volumeEl.textContent = totalVolume.toFixed(2) + ' cm³';
      }
      
      // Update surface area display
      const surfaceAreaEl = document.getElementById('surface-area');
      if (surfaceAreaEl) {
        surfaceAreaEl.textContent = totalSurfaceArea.toFixed(2) + ' cm²';
      }
      
      // Update hidden form fields
      const volumeProp = document.getElementById('volume-prop');
      if (volumeProp) {
        volumeProp.value = totalVolume.toFixed(2) + ' cm³';
      }
      
      const surfaceAreaProp = document.getElementById('surface-area-prop');
      if (surfaceAreaProp) {
        surfaceAreaProp.value = totalSurfaceArea.toFixed(2) + ' cm²';
      }
      
      // Update the form fields that are used for calculations
      const widthInput = document.getElementById('width');
      const heightInput = document.getElementById('height');
      const depthInput = document.getElementById('depth');
      
      if (widthInput && heightInput && depthInput) {
        // Calculate dimensions of a box with equivalent volume
        // We'll use a cube-root to maintain proportions
        const volumeCubeRoot = Math.cbrt(totalVolume * 1000); // Convert back to mm³ before cube root
        
        // Set the dimensions for a proportional box with the same volume
        const width = volumeCubeRoot;
        const height = volumeCubeRoot;
        const depth = volumeCubeRoot;
        
        // Update dimension inputs without triggering their change events
        const isAdjusting = window.isAdjusting;
        window.isAdjusting = true;
        widthInput.value = width.toFixed(1);
        heightInput.value = height.toFixed(1);
        depthInput.value = depth.toFixed(1);
        window.isAdjusting = isAdjusting;
        
        // Update form fields for cart properties
        const widthProp = document.getElementById('width-prop');
        const heightProp = document.getElementById('height-prop');
        const depthProp = document.getElementById('depth-prop');
        
        if (widthProp) widthProp.value = width.toFixed(1) + ' mm';
        if (heightProp) heightProp.value = height.toFixed(1) + ' mm';
        if (depthProp) depthProp.value = depth.toFixed(1) + ' mm';
      }
      
      // After updating volumes, surfaces, and dimensions, trigger cost calculation
      if (typeof window.calculateCost === 'function') {
        window.calculateCost();
        
        // Also update form fields for cart
        if (typeof window.updateFormFields === 'function') {
          window.updateFormFields();
        }
      }
      
      console.log(`Updated combined stats: Volume ${totalVolume.toFixed(2)} cm³, Surface Area ${totalSurfaceArea.toFixed(2)} cm²`);
    }
  }

  // Override the arrange models function to update stats after arrangement
  function addStatsUpdateToArrange() {
    if (typeof window.arrangeModels === 'function') {
      const originalArrangeModels = window.arrangeModels;
      
      window.arrangeModels = function() {
        // Call the original function
        const result = originalArrangeModels.apply(this, arguments);
        
        // Then update the stats
        setTimeout(function() {
          updateCombinedModelStats();
          
          // Force a recalculation of the price
          if (typeof window.calculateCost === 'function') {
            window.calculateCost();
          }
        }, 100);
        
        return result;
      };
      
      console.log('Enhanced arrangeModels function with stats update');
    }
  }
  
  // Patch the load STL part function to trigger updates when new parts are added
  function patchLoadSTLPart() {
    if (typeof window.loadSTLPart === 'function') {
      const originalLoadSTLPart = window.loadSTLPart;
      
      window.loadSTLPart = async function() {
        // Call the original function
        const result = await originalLoadSTLPart.apply(this, arguments);
        
        // After a part is loaded, check if we need to update combined stats
        setTimeout(function() {
          if (window.loadedModelParts && window.loadedModelParts.length > 1) {
            updateCombinedModelStats();
          }
        }, 100);
        
        return result;
      };
      
      console.log('Patched loadSTLPart function to trigger stats updates');
    }
  }
  
  // Patch loadMultipleSTLFiles to trigger updates after all files are loaded
  function patchLoadMultipleSTLFiles() {
    if (typeof window.loadMultipleSTLFiles === 'function') {
      const originalLoadMultipleSTLFiles = window.loadMultipleSTLFiles;
      
      window.loadMultipleSTLFiles = async function() {
        // Call the original function
        const result = await originalLoadMultipleSTLFiles.apply(this, arguments);
        
        console.log('All STL files loaded, updating combined stats');
        
        // Create a series of checks to ensure stats are updated after loading completes
        // Immediate check
        updateCombinedModelStats();
        
        // Multiple delayed checks with increasing intervals
        [200, 500, 1000, 2000].forEach(delay => {
          setTimeout(function() {
            if (window.loadedModelParts && window.loadedModelParts.length > 1) {
              console.log(`Delayed stats update after ${delay}ms`);
              updateCombinedModelStats();
            }
          }, delay);
        });
        
        return result;
      };
      
      console.log('Patched loadMultipleSTLFiles function to trigger stats updates');
    }
  }

  // Override updateVolumeDisplay to handle multiple parts
  function enhanceVolumeDisplayFunction() {
    if (typeof window.updateVolumeDisplay === 'function') {
      const originalUpdateVolumeDisplay = window.updateVolumeDisplay;
      
      window.updateVolumeDisplay = function() {
        // Only use the original function if we don't have multiple parts
        if (!window.loadedModelParts || window.loadedModelParts.length <= 1) {
          originalUpdateVolumeDisplay.apply(this, arguments);
        } else {
          // If we have multiple parts, use our combined stats calculation instead
          updateCombinedModelStats();
        }
      };
      
      console.log('Enhanced updateVolumeDisplay function for multi-part support');
    }
  }
  
  // Override calculateCost to handle the multi-part surface area
  function enhanceCalculateCostFunction() {
    if (typeof window.calculateCost === 'function') {
      const originalCalculateCost = window.calculateCost;
      
      window.calculateCost = function() {
        // For multiple parts, we need to use the surface area directly
        if (window.loadedModelParts && window.loadedModelParts.length > 1) {
          // Get current values
          const infill = parseFloat(document.getElementById('infill').value) / 100;
          const color = document.getElementById('color').value || 'red';
          
          // Get surface area from element
          const surfaceAreaEl = document.getElementById('surface-area');
          let surfaceArea = 0;
          
          if (surfaceAreaEl && surfaceAreaEl.textContent) {
            // Extract the number from "X.XX cm²"
            const match = surfaceAreaEl.textContent.match(/([0-9.]+)/);
            if (match && match[1]) {
              surfaceArea = parseFloat(match[1]);
            }
          }
          
          // Get color price factor
          let colorFactor = 1.2;
          if (color === 'black') colorFactor = 1.0;
          else if (color === 'silver') colorFactor = 1.3;
          else if (color === 'gold') colorFactor = 1.5;
          
          // Calculate base cost using surface area
          const BASE_PRICE_PER_CM2 = 0.0010; // €0.10 per cm²
          
          // Material cost calculation with exponential infill scaling
          const infillImpact = 0.8 + (Math.pow(infill, 1.8) * 1.7);
          const matCost = surfaceArea * BASE_PRICE_PER_CM2 * colorFactor * infillImpact;
          
          // Print time calculation
          const infillTimeFactor = 0.7 + (Math.pow(infill, 1.9) * 2.8);
          const printTime = surfaceArea / 100 * infillTimeFactor;
          
          // Operating costs
          const PRINTER_POWER = 0.25; // kW
          const ENERGY_COST = 0.36; // €/kWh
          const energyCost = printTime * PRINTER_POWER * ENERGY_COST;
          
          // Add fixed costs
          const fixedOpCost = printTime * 1.00 + 20;
          const opCost = energyCost + fixedOpCost;
          
          // Total cost with 50 cent rounding
          const totalCost = Math.ceil((matCost + opCost) * 2) / 2;
          
          // Update display
          document.getElementById('material-cost').textContent = '€' + matCost.toFixed(2);
          document.getElementById('print-time').textContent = printTime.toFixed(1) + 'h';
          document.getElementById('operating-cost').textContent = '€' + opCost.toFixed(2);
          document.getElementById('total-cost').textContent = '€' + totalCost.toFixed(2);
          
          // Update hidden form fields for cart properties
          document.getElementById('time-prop').value = printTime.toFixed(1) + 'h';
          document.getElementById('price-prop').value = '€' + totalCost.toFixed(2);
          
          // Update quantity input with price (1 unit = 1 euro)
          const quantityInput = document.querySelector('input[name="quantity"]');
          if (quantityInput) {
            quantityInput.value = Math.round(totalCost);
          }
          
          console.log(`Calculated price for multi-part model: €${totalCost.toFixed(2)}`);
        } else {
          // Use the original function for single parts
          originalCalculateCost.apply(this, arguments);
        }
      };
      
      console.log('Enhanced calculateCost function for multi-part support');
    }
  }

  // Function to observe model part visibility changes and update stats
  function setupVisibilityObserver() {
    // Watch for changes to part visibility in the multi-part controls
    const modelPartsList = document.getElementById('model-parts-list');
    if (modelPartsList) {
      const observer = new MutationObserver(function(mutations) {
        // Check if any checkboxes were interacted with
        const checkboxMutation = mutations.find(mutation => 
          mutation.target.nodeName === 'INPUT' && 
          mutation.target.type === 'checkbox' &&
          mutation.target.id.startsWith('part-')
        );
        
        if (checkboxMutation) {
          console.log('Part visibility changed, updating combined stats');
          setTimeout(updateCombinedModelStats, 100);
        }
      });
      
      observer.observe(modelPartsList, { 
        attributes: true, 
        childList: true, 
        subtree: true,
        attributeFilter: ['checked']
      });
      
      console.log('Set up visibility observer for multi-part stats');
    }
  }

  // Add stats update to the updateMultiModelUI function
  function enhanceMultiModelUI() {
    if (typeof window.updateMultiModelUI === 'function') {
      const originalUpdateMultiModelUI = window.updateMultiModelUI;
      
      window.updateMultiModelUI = function() {
        // Call the original function
        originalUpdateMultiModelUI.apply(this, arguments);
        
        // Set up visibility observer for checkboxes
        setupVisibilityObserver();
        
        // Then update the combined stats
        setTimeout(updateCombinedModelStats, 100);
      };
      
      console.log('Enhanced updateMultiModelUI function with stats update');
    }
  }

  // Listen for color and infill changes to recalculate price
  function setupPriceUpdateListeners() {
    // Monitor infill slider changes
    const infillSlider = document.getElementById('infill');
    if (infillSlider) {
      infillSlider.addEventListener('input', function() {
        if (window.loadedModelParts && window.loadedModelParts.length > 1) {
          // Recalculate price when infill changes and we have multiple parts
          if (typeof window.calculateCost === 'function') {
            window.calculateCost();
          }
        }
      });
    }
    
    // Monitor color option changes
    const colorOptions = document.querySelectorAll('.color-option');
    colorOptions.forEach(option => {
      option.addEventListener('click', function() {
        if (window.loadedModelParts && window.loadedModelParts.length > 1) {
          // Recalculate price when color changes and we have multiple parts
          setTimeout(function() {
            if (typeof window.calculateCost === 'function') {
              window.calculateCost();
            }
          }, 100);
        }
      });
    });
  }

  // Add a MutationObserver to watch for new parts being added to the DOM
  function setupModelPartsObserver() {
    // Watch for changes to the multi-model-controls element which contains the parts list
    const modelPartsList = document.getElementById('model-parts-list');
    if (modelPartsList) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            console.log('New model parts detected, updating combined stats');
            setTimeout(updateCombinedModelStats, 100);
          }
        });
      });
      
      observer.observe(modelPartsList, { childList: true, subtree: true });
      console.log('Set up observer for new model parts');
    }
  }
  
  // Function to set up all the enhancements
  function setupMultiPartCalculator() {
    // Expose our functions to the global scope
    window.updateCombinedModelStats = updateCombinedModelStats;
    
    // Enhance existing functions with multi-part capabilities
    addStatsUpdateToArrange();
    enhanceVolumeDisplayFunction();
    enhanceMultiModelUI();
    enhanceCalculateCostFunction();
    patchLoadSTLPart();
    patchLoadMultipleSTLFiles();
    
    // Set up observers and listeners
    setupModelPartsObserver();
    setupPriceUpdateListeners();
    
    // Add event listeners for any multi-part control interactions
    const arrangeButton = document.getElementById('arrange-models-btn');
    if (arrangeButton) {
      arrangeButton.addEventListener('click', function() {
        // Update stats after a slight delay to let arrangement complete
        setTimeout(updateCombinedModelStats, 200);
      });
    }
    
    // Monitor model visibility toggles
    const modelControls = document.getElementById('multi-model-controls');
    if (modelControls) {
      modelControls.addEventListener('change', function(e) {
        if (e.target.type === 'checkbox' && e.target.id.startsWith('part-')) {
          // When a part's visibility is toggled, update stats after a delay
          setTimeout(updateCombinedModelStats, 200);
        }
      });
    }
    
    // Initialize once after setup
    setTimeout(updateCombinedModelStats, 500);
    
    // Set up a periodic check for multi-part models (in case they're loaded after our initialization)
    const statsInterval = setInterval(function() {
      if (window.loadedModelParts && window.loadedModelParts.length > 1) {
        updateCombinedModelStats();
      }
    }, 2000);
    
    // Clear interval after 2 minutes to avoid unnecessary processing
    setTimeout(function() {
      clearInterval(statsInterval);
    }, 120000);
    
    // One last safety measure - add a specific callback for when Thingiverse import completes
    if (typeof window.importThingiverseModel === 'function') {
      const originalImportThingiverseModel = window.importThingiverseModel;
      
      window.importThingiverseModel = function(thingId, modelName) {
        // Call the original function
        const result = originalImportThingiverseModel.apply(this, arguments);
        
        // After a model is imported, set up staggered checks
        console.log('Thingiverse import initiated, setting up post-import checks');
        
        // Staggered checks at increasing intervals
        [1000, 2000, 3000, 5000, 8000, 15000].forEach(delay => {
          setTimeout(function() {
            console.log(`Post-import check at ${delay}ms`);
            if (window.loadedModelParts && window.loadedModelParts.length > 1) {
              updateCombinedModelStats();
            }
          }, delay);
        });
        
        return result;
      };
      
      console.log('Enhanced importThingiverseModel function with stats update checks');
    }
    
    console.log('Multi-part calculator setup complete');
  }

  // Run setup on page load
  setupMultiPartCalculator();
  
  // Also try again after a short delay to ensure all elements are loaded
  setTimeout(setupMultiPartCalculator, 1500);
});





